import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Form, Spinner } from 'react-bootstrap';
import { onAuthStateChanged } from 'firebase/auth';
import LayoutFullpage from 'layout/LayoutFullpage';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import HtmlHead from 'components/html-head/HtmlHead';
import { auth, dhReauthenticateWithPassword, dhSignInWithEmailAndPassword } from 'helpers/Firebase';
import { setLogout } from 'auth/authSlice';
import { displaySuccessToast } from 'helpers/toaster';
import { DEFAULT_PATHS } from 'config';
import ApolloClientSellerService from 'ApolloClientSellerService';
import { GET_SECURITY_STATUS } from 'views/catalog/actions/userActions';
import { changePassword, sendLoginOtp, verifyLoginOtp } from './actions';

const OTP_LENGTH = 6;
// Same rule as isStrongPassword in mp-seller-management.
const STRONG_PASSWORD = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!*^?+-_@#$%&]{8,}$/;
const PASSWORD_RULE = 'At least 8 characters, with an upper-case letter, a lower-case letter and a number.';

const ERRORS = {
  OTP_INVALID: 'The code is incorrect.',
  OTP_EXPIRED: 'The code has expired. Please request a new one.',
  OTP_NOT_FOUND: 'Please request a new code.',
  OTP_TOO_MANY_ATTEMPTS: 'Too many wrong attempts. Please request a new code.',
  OTP_SEND_FAILED: 'We could not send the code. Please try again.',
  OTP_REQUIRED: 'Your verification has timed out. Please verify the code again.',
  WEAK_PASSWORD: PASSWORD_RULE,
  PASSWORD_REUSED: 'You cannot reuse your current password or any of your last 3 passwords.',
  CURRENT_PASSWORD_INVALID: 'Your current password is incorrect.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Please try again later.',
  REAUTHENTICATION_REQUIRED: 'Please enter your current password again.',
};

const STEP_LOADING = 'loading';
const STEP_OTP = 'otp';
const STEP_PASSWORD = 'password';

// Login security check: an emailed OTP, then a new password when the current
// one has expired, must be changed, or the user asked to change it
// (?change=password). A password change always starts with a fresh OTP.
const SecurityCheck = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const wantsPasswordChange = new URLSearchParams(location.search).get('change') === 'password';

  const [step, setStep] = useState(STEP_LOADING);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [code, setCode] = useState('');
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [maskedEmail, setMaskedEmail] = useState('');
  const [isSending, setSending] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState('');
  const started = useRef(false);
  const needsPassword = useRef(false);

  const goOn = () => history.replace(location.state?.from || DEFAULT_PATHS.APP);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const sendCode = async () => {
    setSending(true);
    setError('');
    const result = await sendLoginOtp();
    setSending(false);
    if (result?.email) setMaskedEmail(result.email);
    if (result?.retryAfter) setResendIn(result.retryAfter);
    // A cooldown means a code was sent moments ago (e.g. after a refresh),
    // so it isn't an error from the user's point of view.
    if (!result?.success && result?.error !== 'OTP_RESEND_COOLDOWN') {
      setError(ERRORS[result?.error] || ERRORS.OTP_SEND_FAILED);
    }
    return !!result?.success;
  };

  const start = async () => {
    let status = {};
    try {
      const { data } = await ApolloClientSellerService.query({ query: GET_SECURITY_STATUS(), fetchPolicy: 'network-only' });
      status = data?.getUserDetail || {};
    } catch (e) {
      console.error(e);
    }
    needsPassword.current = !!status.passwordChangeRequired || wantsPasswordChange;
    setPasswordExpired(!!status.passwordChangeRequired);
    if (!status.otpVerificationRequired && !needsPassword.current) {
      goOn();
      return;
    }
    setStep(STEP_OTP);
    sendCode();
  };

  // Wait for Firebase to restore the session before calling the API.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        history.replace(DEFAULT_PATHS.LOGIN);
      } else if (!started.current) {
        started.current = true;
        start();
      }
    });
    return unsubscribe;
  }, []);

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    if (code.length !== OTP_LENGTH) {
      setError(`Enter the ${OTP_LENGTH}-digit code from your email.`);
      return;
    }
    setSubmitting(true);
    setError('');
    const result = await verifyLoginOtp(code);
    setSubmitting(false);
    setCode('');
    if (result?.success) {
      if (needsPassword.current) {
        setStep(STEP_PASSWORD);
      } else {
        displaySuccessToast('Verified successfully');
        goOn();
      }
      return;
    }
    const message = ERRORS[result?.error] || 'Verification failed. Please try again.';
    setError(result?.error === 'OTP_INVALID' && result?.attemptsLeft !== undefined ? `${message} ${result.attemptsLeft} attempt(s) left.` : message);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (!STRONG_PASSWORD.test(passwords.next)) {
      setError(PASSWORD_RULE);
      return;
    }
    if (passwords.next !== passwords.confirm) {
      setError('The new passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError('');
    // Check the current password here with Firebase; the backend accepts the
    // change only from a token this fresh.
    try {
      await dhReauthenticateWithPassword(auth.currentUser, passwords.current);
    } catch (e) {
      setSubmitting(false);
      setError(e?.code === 'auth/too-many-requests' ? ERRORS.TOO_MANY_ATTEMPTS : ERRORS.CURRENT_PASSWORD_INVALID);
      return;
    }
    const result = await changePassword(passwords.current, passwords.next);
    if (!result?.success) {
      setSubmitting(false);
      setError(ERRORS[result?.error] || 'Could not change the password. Please try again.');
      if (result?.error === 'OTP_REQUIRED') {
        setStep(STEP_OTP);
        sendCode();
      }
      return;
    }
    // Changing the password ends the old Firebase session; sign in again with
    // the new one so the user carries on without a trip to the login page.
    try {
      await dhSignInWithEmailAndPassword(auth, auth.currentUser?.email, passwords.next);
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
    displaySuccessToast('Your password has been changed');
    goOn();
  };

  const handleLogout = async () => {
    await auth.signOut();
    dispatch(setLogout());
    window.localStorage.authToken = null;
    history.replace(DEFAULT_PATHS.LOGIN);
  };

  const handleResend = async () => {
    if (await sendCode()) displaySuccessToast('A new code has been sent');
  };

  const setPasswordField = (field) => (e) => setPasswords({ ...passwords, [field]: e.target.value });

  let heading = "Verify it's you";
  if (step === STEP_PASSWORD) heading = passwordExpired ? 'Your password has expired' : 'Change your password';

  const otpStep = (
    <>
      <div className="mb-6">
        <p className="h6" style={{ color: 'black' }}>
          {isSending && !maskedEmail ? (
            <>
              Sending a verification code to your email <Spinner animation="border" size="sm" />
            </>
          ) : (
            <>
              We&apos;ve sent a {OTP_LENGTH}-digit code to {maskedEmail || 'your email'}. Enter it below to continue.
            </>
          )}
        </p>
      </div>
      <form id="verifyOtpForm" className="tooltip-end-bottom" onSubmit={handleVerifyCode}>
        <div className="mb-3 filled form-group tooltip-end-top">
          <CsLineIcons icon="lock-off" />
          <Form.Control
            type="text"
            name="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={OTP_LENGTH}
            placeholder="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
            autoFocus
          />
          {error && <div className="d-block invalid-tooltip">{error}</div>}
        </div>
        <Button id="verifyOtpButton" size="lg" type="submit" disabled={isSubmitting || code.length !== OTP_LENGTH} style={{ backgroundColor: '#c4161c' }}>
          {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Verify'}
        </Button>
      </form>
      <div className="mt-5">
        Didn&apos;t get the code? &nbsp;
        <Button variant="outline-primary" size="sm" onClick={handleResend} disabled={isSending || resendIn > 0}>
          <CsLineIcons icon="send" size={14} /> <span>{resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend'}</span>
        </Button>
      </div>
    </>
  );

  const passwordStep = (
    <>
      <div className="mb-6">
        <p className="h6" style={{ color: 'black' }}>
          {passwordExpired ? 'For your security, please choose a new password to continue.' : 'Choose a new password.'}
        </p>
        <p className="text-small text-muted">{PASSWORD_RULE}</p>
      </div>
      <form id="changePasswordForm" className="tooltip-end-bottom" onSubmit={handleChangePassword}>
        <div className="mb-3 filled form-group tooltip-end-top">
          <CsLineIcons icon="lock-off" />
          <Form.Control
            type="password"
            name="currentPassword"
            autoComplete="current-password"
            placeholder="Current password"
            value={passwords.current}
            onChange={setPasswordField('current')}
            autoFocus
          />
        </div>
        <div className="mb-3 filled form-group tooltip-end-top">
          <CsLineIcons icon="lock-on" />
          <Form.Control
            type="password"
            name="newPassword"
            autoComplete="new-password"
            placeholder="New password"
            value={passwords.next}
            onChange={setPasswordField('next')}
          />
        </div>
        <div className="mb-3 filled form-group tooltip-end-top">
          <CsLineIcons icon="lock-on" />
          <Form.Control
            type="password"
            name="confirmPassword"
            autoComplete="new-password"
            placeholder="Confirm new password"
            value={passwords.confirm}
            onChange={setPasswordField('confirm')}
          />
          {error && <div className="d-block invalid-tooltip">{error}</div>}
        </div>
        <Button
          id="changePasswordButton"
          size="lg"
          type="submit"
          disabled={isSubmitting || !passwords.current || !passwords.next || !passwords.confirm}
          style={{ backgroundColor: '#c4161c' }}
        >
          {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Change password'}
        </Button>
      </form>
    </>
  );

  const rightSide = (
    <div className="sw-lg-70 min-h-100 bg-foreground d-flex justify-content-center align-items-center shadow-deep py-5 full-page-content-right-border">
      <div className="sw-lg-50 px-5">
        <div className="mb-7">
          <img src="/img/profile/logo.png" alt="logo" width="400" />
        </div>
        <div className="mb-3">
          <h2 className="cta-1 mb-0" style={{ color: 'black' }}>
            {heading}
          </h2>
        </div>
        {step === STEP_LOADING && <Spinner animation="border" size="sm" />}
        {step === STEP_OTP && otpStep}
        {step === STEP_PASSWORD && passwordStep}
        <div className="mt-3">
          Not you? &nbsp;
          <Button variant="outline-primary" size="sm" onClick={handleLogout}>
            <CsLineIcons icon="arrow-left" size={14} /> <span>Back to login</span>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <HtmlHead title="Security Check" description="Login security check" />
      <LayoutFullpage left={<div className="min-h-100" />} right={rightSide} />
    </>
  );
};

export default SecurityCheck;
